import React, { useMemo, useState } from 'react';
import { useGlobalPositions, useGlobalData } from '../hooks/useGlobalHooks';
import { usePositionStore } from '../contexts/LTPStore/usePositionsStore';
import { useWebSocketStatusStore } from '../contexts/LTPStore/useWebSocketStatusStore';
import WebSocketManager from '../contexts/websocketManager';
import { AlertTriangle, Clock, Trash2 } from 'lucide-react';
import { useClosedPositionStore } from '../contexts/LTPStore/useClosedPositionStore';
import apiClient from '../utils/apiClient';
import toast from 'react-hot-toast';
import server from '../utils/serverConfig';
import ExitSuccessModal from './ExitSuccessModal';
import ExitPayloadModal from './ExitPayloadModal';
// ==========================================
// 1. SHARED COMPONENT: PositionsTable
// ==========================================
/**
 * Get broker badge info based on broker name
 * @param {string} broker - Broker name
 * @returns {Object} Badge info with text and color classes
 */
function getBrokerBadge(broker) {
  const brokerLower = broker?.toLowerCase() || "";

  if (brokerLower === "fyers") {
    return { text: "FY", class: "bg-blue-800 text-white" };
  } else if (brokerLower === "dhan_hq") {
    return { text: "DHQ", class: "bg-emerald-950 text-white" };
  } else if (brokerLower.includes("dhan")) {
    return { text: "DH", class: "bg-emerald-800 text-white" };
  } else if (brokerLower === "nubra") {
    return { text: "NU", class: "bg-purple-800 text-white" };
  } else if (brokerLower === "zerodha") {
    return { text: "ZE", class: "bg-[#EF5350] text-white" };
  }
  return { text: "??", class: "bg-gray-700 text-white" };
}

// This component handles all the sorting, filtering, and HTML rendering.
// It accepts 'data' as a prop so it can be used for Open, Closed, or All.
const PositionsTable = ({ data, loading, useClosedPnl = false, onExitSingle, clientBrokerMap, clientData }) => {
  const SERIAL_KEY = "__row_no__";

  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'symbol', direction: 'desc' });
  const [filterSide, setFilterSide] = useState('ALL');
  const [selectedBroker, setSelectedBroker] = useState('ALL');


  const formatDateTime = (isoString) => {
    if (!isoString || isoString === 0) return "-";

    try {
      // Convert "2025-12-31 20:07:10+00:00" → ISO-safe
      const safeIso = isoString.replace(" ", "T");

      const date = new Date(safeIso);

      return new Intl.DateTimeFormat("en-IN", {
        timeZone: "UTC", // 🔥 KEY FIX
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(date);

    } catch (e) {
      return String(isoString);
    }
  };




  const formatCurrency = (v) => {
    const num = parseFloat(v);
    if (Number.isNaN(num)) return String(v ?? '-');
    try {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(num);
    } catch (e) { return num.toFixed(2); }
  };

  const normalizeSide = (s) => {
    if (s === 1 || s === '1' || String(s).toLowerCase().startsWith('b')) return 'BUY';
    if (s === 2 || s === -1 || s === '2' || String(s).toLowerCase().startsWith('s')) return 'SELL';
    return String(s || 'N/A').toUpperCase();
  };

  const prettifyKey = (k) => {
    if (!k) return '';
    if (k === SERIAL_KEY) return "No.";
    if (k === 'last_updated') return 'Time';
    if (k === 'client_id' || k === 'clientId') return "Client";
    const spaced = k.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
    return spaced.split(' ').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
  };

  // --- Dynamic Columns ---
  const allKeys = useMemo(() => {
    const keys = new Set();
    if (data && data.length > 0) {
      data.forEach(p => {
        if (p && typeof p === "object") {
          Object.keys(p).forEach(k => {
            if (k === "id" || k === "entry" || k === "position_source" || k === "last_order_dt" || k === "pl" || k === "pnl") return;
            keys.add(k);
          });
        }
      });
    }
    const preferred = ["symbol", "last_updated", "client_id", "clientId", "side", "net_qty", "qty", "avg_price", "net_avg", "ltp", "current_ltp", "strategy"];
    const ordered = [];
    preferred.forEach(k => { if (keys.has(k)) { ordered.push(k); keys.delete(k); } });
    return [SERIAL_KEY, ...ordered, ...Array.from(keys).sort()];
  }, [data]);

  // --- Filtering & Sorting ---
  const processed = useMemo(() => {
    if (!data || data.length === 0) return [];
    const q = searchQuery.trim().toLowerCase();

    let filtered = data.filter(p => {
      // Search
      if (q) {
        const fields = [p.symbol, p.client_id || p.clientId, p.strategy, String(p.net_qty || p.qty || ''), String(p.pl || p.pnl || '')];
        if (!fields.some(f => String(f || '').toLowerCase().includes(q))) return false;
      }
      // Side
      const sideNormalized = normalizeSide(p.side);
      if (filterSide !== 'ALL' && sideNormalized !== filterSide) return false;
      return true;
    });

    // Sort
    if (sortConfig.key) {
      const { key, direction } = sortConfig;
      filtered.sort((a, b) => {
        const aRaw = a[key], bRaw = b[key];
        if (key === 'last_updated') {
          const dateA = new Date(aRaw || 0).getTime();
          const dateB = new Date(bRaw || 0).getTime();
          return direction === 'asc' ? dateA - dateB : dateB - dateA;
        }

        // For Symbol: strictly alphabetical
        if (key === 'symbol') {
          return direction === 'asc'
            ? String(aRaw || '').localeCompare(String(bRaw || ''))
            : String(bRaw || '').localeCompare(String(aRaw || ''));
        }

        const parse = (v) => {
          const n = parseFloat(String(v).replace(/[^0-9.-]+/g, ''));
          return Number.isFinite(n) ? n : NaN;
        };
        const aNum = parse(aRaw), bNum = parse(bRaw);
        if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
          return direction === 'asc' ? aNum - bNum : bNum - aNum;
        }
        return String(aRaw || '').localeCompare(String(bRaw || ''));
      });
    }

    // 🚨 FORCE SELL POSITIONS TO TOP (ALL TAB BEHAVIOR)
    filtered.sort((a, b) => {
      const aSell = normalizeSide(a.side) === 'SELL' && (a.net_qty ?? a.qty) < 0;
      const bSell = normalizeSide(b.side) === 'SELL' && (b.net_qty ?? b.qty) < 0;

      if (aSell && !bSell) return -1;
      if (!aSell && bSell) return 1;
      return 0;
    });

    return filtered;
  }, [data, searchQuery, sortConfig, filterSide]);

  const availableBrokers = useMemo(() => {
    const brokers = new Set(['ALL']);
    if (clientData) {
      clientData.forEach(c => {
        if (c.broker) brokers.add(c.broker);
      });
    }
    return Array.from(brokers);
  }, [clientData]);

  const finalFilteredPositions = useMemo(() => {
    if (selectedBroker === 'ALL') return processed;
    return processed.filter(p => {
      const b = clientBrokerMap[p.client_id || p.clientId];
      return b === selectedBroker;
    });
  }, [processed, selectedBroker, clientBrokerMap]);

  // --- Stats ---
  const stats = useMemo(() => {
    const total = finalFilteredPositions ? finalFilteredPositions.length : 0;

    let totalPnl = 0;
    const clients = new Set();

    if (Array.isArray(finalFilteredPositions)) {
      finalFilteredPositions.forEach(p => {
        const closed = parseFloat(
          p.closed_pnl ??
          p.closedPnl ??
          p.pnl ??
          p.pl ??
          0
        ) || 0;

        totalPnl += closed;

        if (p.client_id) clients.add(p.client_id);
        if (p.clientId) clients.add(p.clientId);
      });
    }

    return {
      total,
      totalPnl,
      uniqueClients: clients.size
    };
  }, [finalFilteredPositions]);


  const hasOpenSellPositions = useMemo(() => {
    if (!Array.isArray(data)) return false;
    return data.some(p => {
      const side = normalizeSide(p.side);
      const qty = parseFloat(p.net_qty ?? p.qty ?? 0);
      const symbol = (p.symbol || "").toUpperCase();
      return side === 'SELL' && qty < 0 && !symbol.startsWith("MCX") && !symbol.endsWith("FUT");
    });
  }, [data]);

  // --- Handlers ---
  const handleSort = (key) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const NON_SORTABLE = ["side", SERIAL_KEY];          // hide icon on Side column
  const TIME_COLUMN = "last_updated";       // column that shows OLD/NEW text

  const getSortIcon = (key) => {
    // console.log("KEY------", key);
    // 1. SIDE → no icon at all
    if (NON_SORTABLE.includes(key)) return null;

    // 2. Default unsorted state → small gray icon
    if (sortConfig.key !== key) {
      return (
        <svg className="w-3 h-3 text-gray-600 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    // 3. TIME COLUMN → SHOW OLD/NEW + ARROW
    if (key === TIME_COLUMN) {
      return sortConfig.direction === "asc" ? (
        <span className="flex items-center gap-1 text-blue-400 text-[10px] font-bold">
          OLD
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </span>
      ) : (
        <span className="flex items-center gap-1 text-blue-400 text-[10px] font-bold">
          NEW
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      );
    }

    // 4. Other sortable columns → ONLY ARROW (no text)
    return sortConfig.direction === "asc" ? (
      <svg className="w-3 h-3 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-3 h-3 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };




  if (loading) {
    return (
      <div className="bg-[#1a1d29] p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-blue-500 inline-block" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
          <p className="text-gray-400 mt-4">Loading positions...</p>
        </div>
      </div>
    );
  }

  const isEmpty = !processed || processed.length === 0;


  return (
    <div className="flex flex-col h-full overflow-hidden bg-[#1a1d29]">
      {/* --- Controls Header --- */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 shrink-0 border-b border-gray-800">
        <div className="flex flex-col gap-3 w-full md:w-auto">
          <div className="flex items-center gap-3">
            <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search symbol, strategy, client..." className="bg-[#0f1116] text-white text-sm px-4 py-2 rounded-md border border-gray-700 w-full md:w-80 focus:border-blue-500 focus:outline-none" />
            {hasOpenSellPositions && (
              <div className="flex items-center gap-1 px-2 py-1 rounded bg-red-600/10 border border-red-500/30 text-red-400 text-xs font-bold animate-pulse">
                <AlertTriangle className="w-3.5 h-3.5" />
                SELL POSITIONS OPEN
              </div>
            )}
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span>Rows: {finalFilteredPositions.length}</span>
            <span>Clients: {stats.uniqueClients}</span>
          </div>

          {/* Broker Filter Chips */}
          <div className="flex items-center gap-2 mt-2">
            {availableBrokers.map(b => (
              <button
                key={b}
                onClick={() => setSelectedBroker(b)}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${selectedBroker === b
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
              >
                {b === 'ALL' ? 'All Brokers' : b.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap justify-end">
          <div className="flex bg-gray-800 rounded p-1">
            {['ALL', 'BUY', 'SELL'].map(side => (
              <button key={side} onClick={() => setFilterSide(side)} className={`px-3 py-1 rounded text-xs font-medium transition-colors ${filterSide === side ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}>{side}</button>
            ))}
          </div>
        </div>
      </div>


      {/* --- Table Body --- */}
      <div className="flex-1 min-h-0 overflow-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-[#1a1d29]">
        <div className="inline-block min-w-full align-middle">
          <table className="w-full text-sm text-left whitespace-nowrap">
            <thead className="bg-[#14161f] sticky top-0 z-10 shadow-sm border-b border-gray-700/50">
              <tr>
                {allKeys.map(key => (
                  <th key={key} onClick={() => handleSort(key)} className="py-3 px-4 text-gray-400 font-semibold text-xs uppercase tracking-wider cursor-pointer hover:bg-gray-800 transition-colors select-none">
                    <div className="flex items-center gap-2"><span>{prettifyKey(key)}</span>{getSortIcon(key)}</div>
                  </th>
                ))}
                <th className="py-3 px-4 text-gray-400 text-xs uppercase text-center">
                  Action
                </th>

              </tr>

            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {isEmpty ? (
                <tr>
                  <td colSpan={Math.max(allKeys.length, 1)} className="py-20 text-center text-gray-500">
                    <div className="flex flex-col items-center gap-2"><Clock className="w-8 h-8 opacity-20" /><span>No position data found</span></div>
                  </td>
                </tr>
              ) : (
                finalFilteredPositions.map((p, idx) => {
                  const normalizedSide = normalizeSide(p.side);
                  const qtyVal = parseFloat(p.net_qty ?? p.qty ?? 0);
                  const symbol = (p.symbol || "").toUpperCase();
                  const isNegativeSell = normalizedSide === 'SELL' && qtyVal < 0 && !symbol.startsWith("MCX") && !symbol.endsWith("FUT");

                  return (
                    <tr key={idx} className={`transition-colors hover:bg-[#1f222e] ${isNegativeSell ? "bg-red-900/10" : ""}`}>
                      {allKeys.map((k, colIndex) => {
                        if (k === SERIAL_KEY) {
                          return (
                            <td
                              key={k}
                              className="py-3 px-4 text-gray-400 font-mono text-xs"
                            >
                              {idx + 1}
                            </td>
                          );
                        }
                        const raw = p[k];
                        let cell = (raw === null || typeof raw === 'undefined') ? '-' : (typeof raw === 'object' ? JSON.stringify(raw) : String(raw));

                        if (k === 'last_updated') return <td key={k} className="py-3 px-4 text-gray-400 text-xs font-mono">{formatDateTime(raw)}</td>;

                        if (k === 'client_id' || k === 'clientId') {
                          const broker = clientBrokerMap?.[raw];
                          const badge = getBrokerBadge(broker);

                          return (
                            <td key={k} className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${badge.class}`}
                                >
                                  {badge.text}
                                </span>
                                <span className="text-white font-medium">{raw}</span>
                              </div>
                            </td>
                          );
                        }

                        if (k === 'side') {
                          const isBuy = normalizedSide === 'BUY';
                          return <td key={k} className="py-3 px-4"><span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${isBuy ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>{normalizedSide}</span></td>;
                        }
                        if (k === 'pl' || k === 'pnl') {
                          const num = parseFloat(raw) || 0;
                          return <td key={k} className={`py-3 px-4 font-mono font-bold ${num >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrency(num)}</td>;
                        }
                        if (['net_avg', 'avg_price', 'ltp', 'current_ltp'].includes(k)) {
                          const num = parseFloat(raw);
                          return <td key={k} className="py-3 px-4 text-gray-300 font-mono text-xs">{Number.isFinite(num) ? num.toFixed(2) : cell}</td>;
                        }
                        return <td key={k} className="py-3 px-4 text-gray-300">{colIndex === 0 && isNegativeSell ? <div className="flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5 text-red-500 animate-pulse shrink-0" /><span>{cell}</span></div> : cell}</td>;
                      })}
                      <td className="py-3 px-4 text-center">
                        {onExitSingle ? (
                          <button
                            onClick={() => onExitSingle(p)}
                            title="Exit position"
                            className="text-xs font-semibold transition text-red-400 hover:text-red-300 underline cursor-pointer"
                          >
                            Exit
                          </button>
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </td>


                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div >
  );
};

// ==========================================
// 2. TAB COMPONENT: OpenPositionsTab
// ==========================================
export const OpenPositionsTab = ({ positions, loading, onExitSingle, clientBrokerMap, clientData }) => {
  return (
    <PositionsTable
      data={positions}
      loading={loading}
      onExitSingle={onExitSingle}
      useClosedPnl={true}
      clientBrokerMap={clientBrokerMap}
      clientData={clientData}
    />
  );
};


// ==========================================
// 3. TAB COMPONENT: ClosedPositionsTab
// ==========================================
export const ClosedPositionsTab = ({ closedPositions, clientBrokerMap, clientData }) => {
  // Closed positions usually don't have a specific loading state in some stores, 
  // but if you have one, pass it. Assuming false for now or derived from parent.
  return <PositionsTable data={closedPositions} loading={false} useClosedPnl={false} clientBrokerMap={clientBrokerMap} clientData={clientData} />;
};

// ==========================================
// 4. TAB COMPONENT: AllPositionsTab
// ==========================================
export const AllPositionsTab = ({ positions, closedPositions, loading, clientBrokerMap, clientData }) => {
  // Memoize the combined array to prevent re-renders
  const combinedData = useMemo(() => {
    return [...(positions || []), ...(closedPositions || [])];
  }, [positions, closedPositions]);

  return <PositionsTable data={combinedData} loading={loading} clientBrokerMap={clientBrokerMap} clientData={clientData} />;
};


// ==========================================
// 5. MAIN VIEW: RawPositionsView
// ==========================================
export function RawPositionsView({ clientData }) {
  const { loading } = useGlobalPositions();
  const positions = usePositionStore((state) => state.positions);
  const closed_positions = useClosedPositionStore((state) => state.positions);
  const [activeTab, setActiveTab] = useState('ALL'); // 'ALL' | 'OPEN' | 'CLOSED'
  const tabClasses = (tabName) => `px-6 py-3 text-sm font-medium border-b-2 transition-colors duration-200 ${activeTab === tabName
    ? 'border-blue-500 text-blue-400 bg-blue-500/10'
    : 'border-transparent text-gray-400 hover:text-gray-200 hover:bg-white/5'
    }`;



  const getDefaultOrderType = (symbol) => {
    if (!symbol) return 2;

    const upper = symbol.toUpperCase();
    const INDEXES = ["NIFTY", "BANKNIFTY", "FINNIFTY", "SENSEX"];

    return INDEXES.some(i => upper.includes(i)) ? 1 : 2;
  };



  const clientBrokerMap = useMemo(() => {
    const map = {};
    if (clientData && Array.isArray(clientData)) {
      clientData.forEach(item => {
        map[item.clientId] = item.broker;
      });
    }
    return map;
  }, [clientData]);

  const [orderTypeOverrides, setOrderTypeOverrides] = useState({});


  const normalizeSide = (s) => {
    if (s === 1 || s === '1' || String(s).toLowerCase().startsWith('b')) return 'BUY';
    if (s === 2 || s === -1 || s === '2' || String(s).toLowerCase().startsWith('s')) return 'SELL';
    return String(s || 'N/A').toUpperCase();
  };

  const negativeSellPositions = useMemo(() => {
    if (!Array.isArray(positions)) return [];
    return positions.filter((p) => {
      const side = normalizeSide(p.side);
      const qty = parseFloat(p.net_qty ?? p.qty ?? 0);
      const symbol = (p.symbol || "").toUpperCase();

      return (
        side === "SELL" &&
        qty < 0 &&
        !symbol.startsWith("MCX") &&
        !symbol.endsWith("FUT")
      );
    });
  }, [positions]);

  const [showExitAllModal, setShowExitAllModal] = useState(false);
  const [excludedKeys, setExcludedKeys] = useState(new Set());
  const [showSingleExitModal, setShowSingleExitModal] = useState(false);
  const [singleExitTarget, setSingleExitTarget] = useState(null);

  const getRowKey = (p) =>
    `${p.client_id}|${p.symbol}|${p.strategy}`;

  const exitCandidates = useMemo(() => {
    let candidates = [];
    if (singleExitTarget) {
      candidates = [singleExitTarget];
    } else {
      candidates = negativeSellPositions.filter(
        (p) => !excludedKeys.has(getRowKey(p))
      );
    }

    // Enrich with broker info for the modal
    return candidates.map(p => ({
      ...p,
      broker: clientBrokerMap[p.client_id || p.clientId] || p.broker
    }));
  }, [negativeSellPositions, excludedKeys, singleExitTarget, clientBrokerMap]);

  const handleRemoveFromExit = (p) => {
    setExcludedKeys((prev) => {
      const next = new Set(prev);
      next.add(getRowKey(p));
      return next;
    });
  };


  const handleExitConfirm = () => {
    if (!negativeSellPositions || negativeSellPositions.length === 0) return;
    setShowExitAllModal(true);
  };



  const [showExitSuccessModal, setShowExitSuccessModal] = useState(false);
  const [exitSuccessOrders, setExitSuccessOrders] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const handleExitAllSell = async (qtyOverridesMap = {}, typeOverridesMap = {}) => {
    if (exitCandidates.length === 0) return;

    // Helper to map payload using the new overrides map
    const makePayload = (list) => list.map(p => {
      const key = `${p.client_id || p.clientId}|${p.symbol}|${p.strategy}`;
      const defaultType = getDefaultOrderType(p.symbol);

      let qty = Math.abs(p.net_qty ?? p.qty ?? 0);
      // If override exists, use it
      if (qtyOverridesMap[key]) {
        qty = parseFloat(qtyOverridesMap[key]);
      }

      // Priority: Modal Override -> Existing Override -> Default
      let orderType = typeOverridesMap[key] || orderTypeOverrides[p.client_id + p.symbol] || defaultType;

      return {
        symbol: p.symbol,
        side: p.side,
        strategy: p.strategy,
        netQty: qty,
        clientId: p.client_id || p.clientId,
        broker: clientBrokerMap[p.client_id || p.clientId],
        type: orderType
      };
    });

    // Segregate positions
    const xtsPositions = [];
    const xtsAnuragPositions = [];
    const normalPositions = [];

    exitCandidates.forEach(p => {
      const broker = clientBrokerMap[p.client_id || p.clientId];
      if (broker === 'dhan_xts' || broker === 'dhan_ss') {
        xtsPositions.push(p);
      } else if (broker === 'dhan_xts_anurag' || broker === 'dhan_ss_anurag') {
        xtsAnuragPositions.push(p);
      } else {
        normalPositions.push(p);
      }
    });

    const payloadXTS = makePayload(xtsPositions);
    const payloadXTSAnurag = makePayload(xtsAnuragPositions);
    const payloadNormal = makePayload(normalPositions);

    console.log("EXIT ALL SELL - XTS:", payloadXTS);
    console.log("EXIT ALL SELL - XTS ANURAG:", payloadXTSAnurag);
    console.log("EXIT ALL SELL - NORMAL:", payloadNormal);

    setIsLoading(true);

    const promises = [];

    console.log("DEBUG RAW: Sending to Godseye:", `${server.godseye.baseUrl}exit_position`);
    console.log("DEBUG RAW: Payload Normal:", JSON.stringify(payloadNormal));

    // 1. Request for Normal
    if (payloadNormal.length > 0) {
      promises.push(
        apiClient.post(`${server.godseye.baseUrl}exit_position`, payloadNormal, {
          headers: { "Content-Type": "application/json" },
        }).then(res => {
          console.log("DEBUG RAW: Godseye response successful", res.data);
          return { type: 'normal', data: res.data };
        }).catch(err => {
          console.error("DEBUG RAW: Godseye request failed", err);
          throw err;
        })
      );
    }

    // 2. Request for XTS
    if (payloadXTS.length > 0) {
      promises.push(
        apiClient.post(`${server.ss_dhan.baseUrl}exit-position`, payloadXTS, {
          headers: { "Content-Type": "application/json" }
        }).then(res => ({ type: 'xts', data: res.data }))
      );
    }

    // 3. Request for XTS Anurag
    if (payloadXTSAnurag.length > 0) {
      promises.push(
        apiClient.post(`${server.ss_dhan_anurag.baseUrl}exit-position`, payloadXTSAnurag, {
          headers: { "Content-Type": "application/json" }
        }).then(res => ({ type: 'xts_anurag', data: res.data }))
      );
    }

    try {
      const results = await Promise.allSettled(promises);

      let allFormattedOrders = [];
      let failureCount = 0;

      results.forEach(result => {
        if (result.status === 'fulfilled') {
          const resData = result.value.data;
          // Check for status 200 (or "success")
          if (resData?.status === 200 || resData?.status === 'success') {

            // Handle structure variations:
            // 1. resData.response is Array -> [ {responses: [...]}, {responses: [...]} ]
            // 2. resData.response.responses (Old single broker)
            // 3. resData.responses (New XTS direct)

            let rawResponses = [];
            const backendResponse = resData?.response;

            if (Array.isArray(backendResponse)) {
              // New logic: List of results from multiple brokers (e.g. [XTS_Response, Zerodha_Response])
              backendResponse.forEach(item => {
                if (!item) return;

                // If the item has a 'responses' key (common pattern)
                if (item.responses && Array.isArray(item.responses)) {
                  rawResponses.push(...item.responses);
                }
                // Or if the item itself is the list of orders (unlikely but possible)
                else if (Array.isArray(item)) {
                  rawResponses.push(...item);
                }
              });
            } else if (backendResponse?.responses && Array.isArray(backendResponse.responses)) {
              // Old logic: Single object wrapper
              rawResponses = backendResponse.responses;
            } else if (resData?.responses && Array.isArray(resData.responses)) {
              // Fallback: Direct property on root
              rawResponses = resData.responses;
            }

            // Flatten in case of nested arrays and safeguard
            rawResponses = (rawResponses || []).flat();

            const formatted = rawResponses.map((item) => ({
              clientId: item.client_id,
              symbol: item.symbol,
              broker: item.broker,
              // Handle ID variations
              orderId: item.response?.result?.AppOrderID || item.response?.order_id || item.response?.id,
              apiStatus: item.response?.order_status || item.status || "Sent",
              status: "Waiting",
              error: item.error || null
            }));

            allFormattedOrders = [...allFormattedOrders, ...formatted];
          } else {
            console.error("Exit API returned non-200 status:", resData);
            failureCount++;
          }
        } else {
          console.error("Exit Request Failed:", result.reason);
          failureCount++;
        }
      });
      console.log("Formatted Order", allFormattedOrders);
      if (allFormattedOrders.length > 0) {
        toast.success(`Exit Initiated for ${allFormattedOrders.length} positions.`);
        setExitSuccessOrders(allFormattedOrders);
        setShowExitAllModal(false);
        setExcludedKeys(new Set());
        setShowExitSuccessModal(true);
      }

      if (failureCount > 0) {
        toast.error(`${failureCount} batch request(s) returned error.`);
      }

    } catch (error) {
      console.error("Critical error in exit all sell:", error);
      toast.error("Critical error executing exit requests");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExitSingleClick = (p) => {
    console.log("Single Exit Clicked", p);
    setExcludedKeys(new Set());      // reset filters
    setSingleExitTarget(p);          // optional
    setShowExitAllModal(true);
  };





  const closeExitModal = () => {
    setShowExitAllModal(false);
    setSingleExitTarget(null);
    setExcludedKeys(new Set());
  };


  return (
    <div className="flex flex-col h-full bg-[#1a1d29]">
      {/* Tab Navigation */}
      <div className="flex border-b border-gray-700 bg-[#14161f]">
        <button
          onClick={() => setActiveTab('ALL')}
          className={tabClasses('ALL')}
        >
          All Positions
        </button>
        <button
          onClick={() => setActiveTab('OPEN')}
          className={tabClasses('OPEN')}
        >
          Open Positions
        </button>
        <button
          onClick={() => setActiveTab('CLOSED')}
          className={tabClasses('CLOSED')}
        >
          Closed Positions
        </button>
      </div>

      {negativeSellPositions.length > 0 && (
        <div className="flex justify-end px-4 py-2">
          <button
            onClick={handleExitConfirm}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full
                 bg-red-600 text-[11px] font-semibold text-white
                 hover:bg-red-500 animate-pulse shadow"
          >
            Exit ALL SELL
            <span className="px-1.5 py-0.5 rounded bg-black/30 text-[10px] font-bold">
              {negativeSellPositions.length}
            </span>
          </button>
        </div>
      )}

      {/* SHARED EXIT PAYLOAD MODAL */}
      <ExitPayloadModal
        isOpen={showExitAllModal}
        onClose={closeExitModal}
        positions={exitCandidates}
        onConfirm={(finalQtys, finalTypes) => handleExitAllSell(finalQtys, finalTypes)}
        onRemove={(p) => handleRemoveFromExit(p)}
        isLoading={isLoading}
      />





      <ExitSuccessModal
        isOpen={showExitSuccessModal}
        onClose={() => setShowExitSuccessModal(false)}
        orders={exitSuccessOrders}
      />




      {/* Tab Content */}
      <div className="flex-1 overflow-hidden relative">
        {activeTab === 'ALL' && (
          <AllPositionsTab
            positions={positions}
            closedPositions={closed_positions}
            loading={loading}
            clientBrokerMap={clientBrokerMap}
            clientData={clientData}
          />
        )}
        {activeTab === 'OPEN' && (
          <OpenPositionsTab
            positions={positions}
            loading={loading}
            onExitSingle={handleExitSingleClick}
            clientBrokerMap={clientBrokerMap}
            clientData={clientData}
          />
        )}
        {activeTab === 'CLOSED' && (
          <ClosedPositionsTab
            closedPositions={closed_positions}
            clientBrokerMap={clientBrokerMap}
            clientData={clientData}
          />
        )}
      </div>
    </div>
  );
}

export default RawPositionsView;